
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Check, 
  X, 
  Clock, 
  ChevronDown, 
  ChevronRight,
  Copy,
  RotateCcw,
  AlertCircle,
  Database,
  List,
  Hash,
  ExternalLink,
  Bot
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ActionCardProps } from '@/types/action-card';
import { PayloadRenderer } from './utils/PayloadRenderer';

export const ActionCardBase = ({ data, className }: ActionCardProps) => {
  const [isExpanded, setIsExpanded] = useState(!data.isCollapsible);
  const [copied, setCopied] = useState(false);

  const statusConfig = {
    success: {
      icon: <Check className="h-3.5 w-3.5" />,
      color: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-50/80 dark:bg-emerald-950/80",
      border: "border-emerald-200/60 dark:border-emerald-800/60",
      badge: "default" as const,
      dot: "bg-emerald-500"
    },
    error: {
      icon: <X className="h-3.5 w-3.5" />,
      color: "text-red-600 dark:text-red-400", 
      bg: "bg-red-50/80 dark:bg-red-950/80",
      border: "border-red-200/60 dark:border-red-800/60",
      badge: "destructive" as const,
      dot: "bg-red-500"
    },
    pending: {
      icon: <Clock className="h-3.5 w-3.5 animate-pulse" />,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50/80 dark:bg-amber-950/80", 
      border: "border-amber-200/60 dark:border-amber-800/60",
      badge: "secondary" as const,
      dot: "bg-amber-500 animate-pulse"
    }
  };

  const config = statusConfig[data.status];

  const getPayloadInfo = () => {
    if (!data.payload) return { icon: <AlertCircle className="h-3.5 w-3.5" />, label: 'No data', count: 0 };
    
    if (Array.isArray(data.payload)) {
      return { 
        icon: <List className="h-3.5 w-3.5" />, 
        label: 'Array', 
        count: data.payload.length 
      };
    }
    
    if (typeof data.payload === 'object') {
      return { 
        icon: <Database className="h-3.5 w-3.5" />, 
        label: 'Object', 
        count: Object.keys(data.payload).length 
      };
    }
    
    return { 
      icon: <Hash className="h-3.5 w-3.5" />, 
      label: 'Value', 
      count: 1 
    };
  };

  const payloadInfo = getPayloadInfo();

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data.payload, null, 2));
      setCopied(true);
      toast.success('Response copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleRetry = () => {
    toast.info('Retrying action...');
    // Here you would typically dispatch a retry action
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timestamp;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className={cn("w-full flex gap-3 min-w-0", className)}
    >
      {/* Copilot Avatar */}
      <div className="flex-shrink-0 mt-1">
        <Avatar className="h-8 w-8">
          <AvatarImage src="/copilot-avatar.png" alt="AI Copilot" />
          <AvatarFallback className="bg-primary/10 text-primary">
            <Bot className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Card Content - Responsive */}
      <div className="flex-1 min-w-0 max-w-full">
        <Card className={cn(
          "border shadow-sm backdrop-blur-sm",
          config.border,
          "transition-all duration-200 hover:shadow-md",
          "bg-card/95 w-full"
        )}>
          {/* Compact Header */}
          <div className={cn(
            "px-3 py-2.5 border-b border-border/50",
            config.bg,
            "flex items-center justify-between gap-2 min-w-0"
          )}>
            {/* Status indicator and content */}
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              {/* Status dot */}
              <div className={cn("w-2 h-2 rounded-full flex-shrink-0", config.dot)} />
              
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5 min-w-0">
                  <h3 className="font-medium text-sm text-foreground truncate">
                    {data.title}
                  </h3>
                  <Badge variant={config.badge} className="text-[10px] px-1.5 py-0 font-medium flex-shrink-0">
                    {data.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground/80 truncate">
                  {data.summary}
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {data.payload && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyAll}
                  className="h-6 w-6 p-0 hover:bg-background/80"
                  disabled={copied}
                >
                  {copied ? (
                    <Check className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              )}
              
              {data.status === 'error' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRetry}
                  className="h-6 w-6 p-0 hover:bg-background/80"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              )}
              
              {data.isCollapsible && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-6 w-6 p-0 hover:bg-background/80"
                >
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </motion.div>
                </Button>
              )}
            </div>
          </div>

          {/* Expandable Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                {data.payload ? (
                  <div className="px-3 py-3">
                    <div className="flex items-center justify-between mb-2.5 gap-2 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="text-muted-foreground/70 flex-shrink-0">
                          {payloadInfo.icon}
                        </div>
                        <span className="text-xs font-medium text-muted-foreground truncate">
                          {payloadInfo.label} Response
                        </span>
                        {payloadInfo.count > 0 && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                            {payloadInfo.count} {payloadInfo.count === 1 ? 'item' : 'items'}
                          </Badge>
                        )}
                      </div>
                      
                      <span className="text-[10px] text-muted-foreground/60 font-mono flex-shrink-0">
                        {formatTimestamp(data.timestamp)}
                      </span>
                    </div>
                    
                    <div className="bg-muted/40 rounded-md p-2.5 border border-border/40 overflow-hidden">
                      <PayloadRenderer payload={data.payload} compact={true} />
                    </div>
                  </div>
                ) : (
                  <div className="px-3 py-4 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground/60">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-xs">No response data available</span>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Compact Footer - only show when collapsed */}
          {!isExpanded && (
            <div className="px-3 py-1.5 bg-muted/20 border-t border-border/30">
              <div className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-muted-foreground/60 flex-shrink-0">
                    {payloadInfo.icon}
                  </div>
                  <span className="text-[10px] text-muted-foreground/70 truncate">
                    {payloadInfo.label}
                  </span>
                  {payloadInfo.count > 0 && (
                    <span className="text-[10px] text-muted-foreground/60 flex-shrink-0">
                      ({payloadInfo.count})
                    </span>
                  )}
                </div>
                
                <span className="text-[10px] text-muted-foreground/50 font-mono flex-shrink-0">
                  {formatTimestamp(data.timestamp)}
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </motion.div>
  );
};
